import React, { useState } from 'react';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LicenseModal from './LicenseModal';
import api from '../../../../config/axiosInstance';
import { logApiError } from '../../../../config/apiErrorHandling';
import { useSnackbar } from 'notistack';

const formatCalendarLabel = (calendar) => {
  const position = calendar?.position_name || `Área ${calendar?.position_id ?? '—'}`;
  const start = calendar?.start_date || '—';
  const end = calendar?.end_date || '—';
  return `${position} (${start} a ${end})`;
};

const getAffectedCalendarsText = (calendars) => {
  const total = Array.isArray(calendars) ? calendars.length : 0;
  return total > 0 ? ` Se actualizaron ${total} calendario(s).` : '';
};

const LicenseTable = ({ licenses, ci, fetchLicenses, isEmployeeInactive, canManageLicenses = true }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [blockedCalendars, setBlockedCalendars] = useState([]);
  const [implicatedCalendars, setImplicatedCalendars] = useState([]);
  const [pendingDeleteDate, setPendingDeleteDate] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleOpenModal = () => {
    if (isEmployeeInactive || !canManageLicenses) return;
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // El backend a veces devuelve date_from en formato HTTP-date (p.ej. "Wed, 01 Jul 2026
  // 00:00:00 GMT") en vez de ISO. El endpoint de borrado solo acepta "YYYY-MM-DD", así
  // que hay que normalizarla acá antes de reenviarla (mismo criterio UTC que formatDate).
  const toIsoDate = (value) => {
    if (!value) return value;

    const isoMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return isoMatch[0];

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${year}-${month}-${day}`;
  };

  const handleDeleteLicense = async (date_from, { confirm = false } = {}) => {
    if (isEmployeeInactive || !canManageLicenses) return;
    if (isDeleting) return;

    const isoDate = toIsoDate(date_from);
    setIsDeleting(true);
    try {
      let url = `personal/edit_license_delete?ci=${encodeURIComponent(ci)}&date_from=${encodeURIComponent(isoDate)}`;
      if (confirm) {
        url += '&confirm=true';
      }
      const { data } = await api.delete(url);
      enqueueSnackbar(`Licencia eliminada correctamente.${getAffectedCalendarsText(data?.affected_calendars)}`, {
        variant: 'success',
      });
      setImplicatedCalendars([]);
      setPendingDeleteDate(null);
      await fetchLicenses();
    } catch (error) {
      const info = logApiError(
        'license.delete.personal',
        error,
        'No se pudo eliminar la licencia. Intenta nuevamente.'
      );
      if (info.status === 409 && info.needsConfirmation) {
        setPendingDeleteDate(isoDate);
        setImplicatedCalendars(info.implicatedCalendars);
        return;
      }
      if (info.status === 409 && info.blockedCalendars.length > 0) {
        setBlockedCalendars(info.blockedCalendars);
      }
      enqueueSnackbar(info.message, { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmImplicatedCalendars = () => {
    if (!pendingDeleteDate) return;
    handleDeleteLicense(pendingDeleteDate, { confirm: true });
  };

  const handleCancelImplicatedCalendars = () => {
    setImplicatedCalendars([]);
    setPendingDeleteDate(null);
  };

  if (!licenses || !licenses.name_columns || !licenses.table_license) {
    return null;
  }

  const { name_columns, table_license, title } = licenses;

  const formatDate = (dateString) => {
    if (!dateString) return '';

    const isoMatch = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return `${day}/${month}/${year}`;
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return String(dateString);
    }

    // Las licencias son fechas calendario, no instantes horarios.
    // Usamos UTC para evitar que se corran al día anterior por zona horaria.
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <TableContainer component={Paper}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <IconButton 
          onClick={handleOpenModal} 
          disabled={isEmployeeInactive || !canManageLicenses}
          sx={{
            color: (isEmployeeInactive || !canManageLicenses) ? 'grey.500' : '#FEC421',
          }}
        >
          <AddIcon />
      </IconButton>
      </Box>
      <Table>
        <TableHead>
          <TableRow>
            {name_columns.map((col) => (
              <TableCell 
                key={col.key} 
                sx={{ 
                  color: '#03447B',
                  fontWeight: 'bold',
                }}
              >
                {col.name}
              </TableCell>
            ))}
            <TableCell sx={{ color: '#03447B', fontWeight: 'bold' }}>Acciones</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {table_license.map((license, index) => (
            <TableRow key={index}>
              {name_columns.map((col) => (
                <TableCell key={col.key}>
                  {col.key === 'date_from' || col.key === 'date_to' 
                    ? formatDate(license[col.key]) 
                    : license[col.key]}
                </TableCell>
              ))}
              <TableCell>
                <IconButton
                  onClick={() => handleDeleteLicense(license.date_from)}
                  disabled={isEmployeeInactive || !canManageLicenses || isDeleting}
                  sx={{
                    color: (isEmployeeInactive || !canManageLicenses) ? '#B0B0B0' : '#D32F2F',
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <LicenseModal
        open={isModalOpen}
        onClose={handleCloseModal}
        ci={ci}
        fetchLicenses={fetchLicenses}
      />
      <Dialog
        open={blockedCalendars.length > 0}
        onClose={() => setBlockedCalendars([])}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Calendarios cerrados implicados</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            No se pudo eliminar la licencia porque hay calendarios cerrados para ese empleado en la fecha seleccionada.
          </Alert>
          <List dense>
            {blockedCalendars.map((calendar, index) => (
              <ListItem key={`${calendar.calendar_id || index}`}>
                <ListItemText
                  primary={formatCalendarLabel(calendar)}
                  secondary={`Estado: ${calendar?.status || '—'}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBlockedCalendars([])}>Cerrar</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={implicatedCalendars.length > 0}
        onClose={isDeleting ? undefined : handleCancelImplicatedCalendars}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Calendario activo implicado</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            El empleado tiene un calendario activo en el rango de esta licencia. Quitar la
            licencia modificará la planificación actual. ¿Deseas continuar?
          </Alert>
          <List dense>
            {implicatedCalendars.map((calendar, index) => (
              <ListItem key={`${calendar.calendar_id || index}`}>
                <ListItemText
                  primary={formatCalendarLabel(calendar)}
                  secondary={`Estado: ${calendar?.status || '—'}`}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelImplicatedCalendars} disabled={isDeleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmImplicatedCalendars}
            variant="contained"
            color="warning"
            disabled={isDeleting}
          >
            Sí, continuar
          </Button>
        </DialogActions>
      </Dialog>
    </TableContainer>
  );
};

export default LicenseTable;
